<template>
  <div class="schedule-settings">
    <div class="schedule-settings__row">
      <span class="schedule-settings__row__input-prefix-label">
        {{ t("startTime") }}
      </span>

      <time-picker
        v-model="start.startTime.value"
        @change="updateEndTimeRange"
        :disabled="disabled"
        :show-validation="rules.startTimeScheduleRule(start.startTime.value)"
        rbt-target="startDate"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            v-on="on"
            v-bind="attrs"
            v-model="start.startTimeDate.value"
            @click:clear="start.startTime.value = null"
            :disabled="disabled"
            :rules="[() => rules.startTimeScheduleRule(start.startTime.value)]"
            rbt-target="inputStartDate"
            append-icon="mdi-calendar-blank"
            hide-details
            readonly
            clearable
          >
            <template v-slot:append>
              <v-icon v-on="on">mdi-calendar-blank</v-icon>
            </template>
          </v-text-field>
        </template>
      </time-picker>

      <span
        v-if="start.startTimeUTC.value"
        :class="['schedule-settings__row__input-prefix-label', 'schedule-settings__row__input-prefix-label--fade-true']"
      >
        {{ start.startTimeUTC.value }} UTC
      </span>
    </div>

    <div class="schedule-settings__row--type-control">
      <v-checkbox
        v-model="automaticallyShutdown"
        @change="updateEndTimeRange"
        :disabled="!start.startTime.value || disabled"
        :label="t('checkboxShutdownSession')"
        hide-details
      />

      <ul class="schedule-settings__control">
        <li class="schedule-settings__control__item">
          <v-btn
            @click="applyShutdownTimer(1)"
            :class="[isUsingThisHour(1) && !useCustomEndTime && 'schedule-settings__control__item__btn--state-active']"
            :disabled="!automaticallyShutdown || disabled"
            large
            >1 {{ t("hour", undefined, 1) }}</v-btn
          >
        </li>

        <li class="schedule-settings__control__item">
          <v-btn
            @click="applyShutdownTimer(3)"
            :class="[isUsingThisHour(3) && !useCustomEndTime && 'schedule-settings__control__item__btn--state-active']"
            :disabled="!automaticallyShutdown || disabled"
            large
            >3 {{ t("hour", undefined, 3) }}</v-btn
          >
        </li>

        <li class="schedule-settings__control__item">
          <v-btn
            @click="applyShutdownTimer(6)"
            :class="[isUsingThisHour(6) && !useCustomEndTime && 'schedule-settings__control__item__btn--state-active']"
            :disabled="!automaticallyShutdown || disabled"
            large
            >6 {{ t("hour", undefined, 6) }}</v-btn
          >
        </li>

        <li class="schedule-settings__control__item">
          <v-btn
            @click="toggleCustomEndTime"
            :class="useCustomEndTime && 'schedule-settings__control__item__btn--state-active'"
            :disabled="!automaticallyShutdown || disabled"
            large
          >
            {{ t("specificDate") }}
          </v-btn>
        </li>
      </ul>
    </div>

    <div v-if="automaticallyShutdown && useCustomEndTime && start.startTime.value" class="schedule-settings__row">
      <span class="schedule-settings__row__input-prefix-label">
        {{ t("endTime") }}
      </span>

      <time-picker
        v-model="end.endTime.value"
        :min-date="start.startTime.value"
        :disabled="disabled"
        :show-validation="rules.endTimeScheduleRule(end.endTime.value, start.startTime.value)"
        rbt-target="endDate"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            v-on="on"
            v-bind="attrs"
            v-model="end.endTimeDate.value"
            @click:clear="end.endTime.value = null"
            :disabled="disabled"
            :rules="[() => rules.endTimeScheduleRule(end.endTime.value, start.startTime.value)]"
            rbt-target="inputEndDate"
            append-icon="mdi-calendar-blank"
            hide-details
            readonly
            clearable
          >
            <template v-slot:append>
              <v-icon v-on="on">mdi-calendar-blank</v-icon>
            </template>
          </v-text-field>
        </template>
      </time-picker>

      <span
        v-if="end.endTimeUTC.value"
        :class="['schedule-settings__row__input-prefix-label', 'schedule-settings__row__input-prefix-label--fade-true']"
      >
        {{ end.endTimeUTC.value }} UTC
      </span>
    </div>

    <online-for
      v-if="start.startTime.value && end.endTime.value"
      :start="start.startTime.value"
      :end="end.endTime.value"
      :disabled="disabled"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "@vue/composition-api"
import { useSchedule } from "./composable/useSchedule"
import { createTranslationModule } from "@/plugins/i18n"
import * as rules from "../lib/schedule-rules"
import TimePicker from "./_TimePicker.vue"
import OnlineFor from "../_OnlineFor.vue"
import Utils from "@/utils/utils"

export default defineComponent({
  name: "SchedulerSettings",
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { start, end } = useSchedule()
    const t = createTranslationModule("AutoShutdown.scheduleSession")
    const automaticallyShutdown = ref(false)
    const useCustomEndTime = ref(false)
    const lastHourApplied = ref(1)

    const applyShutdownTimer = (hours: number) => {
      const startTime = start.startTime.value

      if (startTime) {
        end.endTime.value = startTime + Utils.hours(hours)
        lastHourApplied.value = hours
        useCustomEndTime.value = false
      }
    }

    const isUsingThisHour = (hour: number) => {
      return lastHourApplied.value === hour && automaticallyShutdown.value
    }

    const toggleCustomEndTime = () => {
      useCustomEndTime.value = !useCustomEndTime.value
    }

    const updateEndTimeRange = () => {
      if (automaticallyShutdown.value) {
        applyShutdownTimer(lastHourApplied.value)
      } else {
        end.endTime.value = null
      }
    }

    const _loadSavedSettings = () => {
      if (start.startTime.value && end.endTime.value) {
        const hourRange = Utils.dateRange(start.startTime.value, end.endTime.value)
        const rangeOptions = [1, 3, 6]

        const isPredefinedHourOption = rangeOptions.some(hour => {
          const isMatch = hourRange.hours === hour

          if (isMatch) {
            lastHourApplied.value = hour
          }

          return isMatch
        })

        automaticallyShutdown.value = true
        useCustomEndTime.value = !isPredefinedHourOption
      } else if (!start.startTime.value || start.startTime.value <= Date.now()) {
        start.startTime.value = Date.now() + Utils.hours(1)
      }
    }
    _loadSavedSettings()

    return {
      t,
      rules,
      isUsingThisHour,
      updateEndTimeRange,
      applyShutdownTimer,
      automaticallyShutdown,
      useCustomEndTime,
      toggleCustomEndTime,
      start,
      end
    }
  },
  components: {
    OnlineFor,
    TimePicker
  }
})
</script>
